import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { signUpUser } from '../../../api/services/userServices';
import { Menu, X, User, Mail, Lock, Facebook, Twitter, Instagram, Linkedin, Phone, MapPin } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = z.object({
    first_name: z.string().min(1, { message: "First name is required." }),
    last_name: z.string().min(1, { message: "Last name is required." }),
    email: z.string().email({ message: "Invalid email address." }),
    phone_number: z.string().min(10, { message: "Phone number must be at least 10 digits." }),
    role: z.enum(["guest", "host"]),
    password: z.string().min(6, { message: "Password must be at least 6 characters." }),
    confirmPassword: z.string().min(6, { message: "Confirm password must be at least 6 characters." }),
    terms: z.boolean().refine(val => val === true, { message: "You must accept the terms and conditions." }),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ["confirmPassword"],
});

const termsSections: { title: string; body: string }[] = [
    {
        title: '1. Acceptance of Terms',
        body:
            'By creating an account on Social Bunkr, you agree to be bound by these Terms and Conditions and our Privacy Policy. If you do not agree, please do not use the platform.',
    },
    {
        title: '2. Eligibility',
        body:
            'You must be at least 18 years of age to register and use Social Bunkr. By signing up, you confirm that the information you provide is accurate and complete.',
    },
    {
        title: '3. Account Responsibility',
        body:
            'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use.',
    },
    {
        title: '4. Use of Service',
        body:
            'You agree to use Social Bunkr only for lawful purposes. Any misuse, fraudulent activity, or violation of applicable laws may result in account suspension or termination.',
    },
    {
        title: '5. Listings and Bookings',
        body:
            'Hosts are responsible for the accuracy of their listings, including availability, pricing, and property details. Guests are responsible for honoring their bookings and following house rules.',
    },
    {
        title: '6. Payments',
        body:
            'All payments are processed through our secure payment gateway. Social Bunkr is not liable for any disputes arising from third-party payment processors.',
    },
    {
        title: '7. Privacy',
        body:
            'Your personal information is handled in accordance with our Privacy Policy. We do not share your data with third parties without your consent, except as required by law.',
    },
    {
        title: '8. Limitation of Liability',
        body:
            'Social Bunkr is provided on an "as is" basis. We are not liable for any direct, indirect, or consequential damages arising from your use of the platform.',
    },
    {
        title: '9. Changes to Terms',
        body:
            'We reserve the right to update these Terms and Conditions at any time. Continued use of the service after changes constitutes acceptance of the revised terms.',
    },
    {
        title: '10. Contact',
        body:
            'For any questions regarding these Terms, please contact our support team through the help section of the website.',
    },
];

const SignUp: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            first_name: "",
            last_name: "",
            email: "",
            phone_number: "",
            role: "guest",
            password: "",
            confirmPassword: "",
            terms: false,
        },
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            const response = await signUpUser({
                email: values.email,
                first_name: values.first_name,
                last_name: values.last_name,
                role: values.role,
                password: values.password,
                phone_number: values.phone_number,
            });

            if (response?.message) {
                toast.success('Sign-up successful!', { position: 'top-center', autoClose: 2000 });
                form.reset();

                const redirectPath = new URLSearchParams(location.search).get('redirect');
                const queryString = location.search.split('redirect=')[1] ? location.search.split('redirect=')[1].split('&').slice(1).join('&') : '';

                if (redirectPath) {
                    navigate(`${redirectPath}?${queryString}`);
                } else {
                    navigate('/login');
                }
            } else {
                const data = response as any;
                if (data?.phone_number) {
                    const msg = Array.isArray(data.phone_number) ? data.phone_number[0] : data.phone_number;
                    form.setError('phone_number', { message: msg });
                    toast.error(msg, { position: 'top-center', autoClose: 2000 });
                } else if (data?.email) {
                    const msg = Array.isArray(data.email) ? data.email[0] : data.email;
                    form.setError('email', { message: msg });
                    toast.error(msg, { position: 'top-center', autoClose: 2000 });
                } else {
                    const errorMessage = data?.message || data?.detail || 'An unexpected error occurred.';
                    toast.error(errorMessage, { position: 'top-center', autoClose: 2000 });
                }
            }
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message || 'An error occurred during sign-up.';
            toast.error(errorMessage, { position: 'top-center', autoClose: 2000 });
        }
    };

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
            >
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link
                  key={item}
                  to={item === "Home" ? "/" : `/${item.toLowerCase()}`} // Use / for root path
                  className="text-foreground/80 hover:text-primary font-medium transition-colors"
                >
                  {item}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-foreground hover:text-primary">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Register
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-border"
            >
              <nav className="flex flex-col gap-4">
                {["Home", "About", "Contact"].map((item) => (
                  <Link
                    key={item}
                    to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                    className="text-foreground/80 hover:text-primary font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item}
                  </Link>
                ))}
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <Link to="/login">
                    <Button variant="ghost" className="justify-start">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-primary text-primary-foreground">
                      Register
                    </Button>
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-1 flex items-center justify-center pt-20 md:pt-24 pb-16 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-card rounded-2xl shadow-lg p-8 my-8"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-primary text-center mb-2">
            Create Your Account
          </h1>
          <p className="text-foreground/70 text-center mb-6">
            Join Social Bunkr and discover amazing places.
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {/* User Type Toggle */}
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>I am a...</FormLabel>
                    <FormControl>
                      <div className="flex rounded-lg border border-input overflow-hidden">
                        <button
                          type="button"
                          onClick={() => field.onChange("guest")}
                          className="flex-1 py-2.5 text-sm font-medium transition-colors bg-primary text-primary-foreground"
                        >
                          Guest
                        </button>
                        {/* Host registration moved to Occupi — entry point hidden */}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <FormField
                      control={form.control}
                      name="first_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              id="first_name"
                              placeholder="John"
                              className="pl-10"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <FormField
                      control={form.control}
                      name="last_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              id="last_name"
                              placeholder="Doe"
                              className="pl-10"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            className="pl-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            id="phone_number"
                            type="tel"
                            placeholder="123-456-7890"
                            className="pl-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            className="pl-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            id="confirmPassword"
                            type="password"
                            placeholder="••••••••"
                            className="pl-10"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <FormField
                  control={form.control}
                  name="terms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border-0 p-0">
                      <FormControl>
                        <Checkbox
                          id="terms"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                          I agree to the{" "}
                          <button
                            type="button"
                            onClick={() => setShowTermsModal(true)}
                            className="text-primary hover:underline font-medium"
                          >
                            Terms and Conditions
                          </button>
                        </Label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={!form.formState.isValid}
              >
                Create Account
              </Button>
            </form>
          </Form>

          <p className="text-center text-foreground/70 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Log in
            </Link>
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="mb-6">
                <Link to="/" className="text-2xl font-bold">
                  <span className="text-primary-foreground">Social</span>
                  <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
                </Link>
              </div>
              <p className="text-background/70 mb-6 leading-relaxed">
                Bharat's most affordable stay platform. Experience comfortable living with all-inclusive amenities.
              </p>
              <div className="flex gap-4">
                <motion.a
                  href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.1 }}
                  className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors"
                >
                  <Facebook size={18} />
                </motion.a>
                <motion.a
                  href="https://twitter.com/" target="_blank" rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.1 }}
                  className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors"
                >
                  <Twitter size={18} />
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.1 }}
                  className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors"
                >
                  <Instagram size={18} />
                </motion.a>
                <motion.a
                  href="#" // Placeholder for Linkedin
                  whileHover={{ y: -3, scale: 1.1 }}
                  className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors"
                >
                  <Linkedin size={18} />
                </motion.a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-background/70 hover:text-secondary transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    How it Works
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Destinations
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Careers
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    FAQs
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-background/70 hover:text-secondary transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-background/70 hover:text-secondary transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={20} className="text-secondary flex-shrink-0 mt-1" />
                  <span className="text-background/70">
                    Brundavan colony, Plot no 105, Kokapet, Hyderabad, Telangana 500075
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={20} className="text-secondary flex-shrink-0" />
                  <span className="text-background/70">+91-8008585553</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={20} className="text-secondary flex-shrink-0" />
                  <span className="text-background/70">hello@socialbunkr.in</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-background/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-background/60 text-sm">
                © {new Date().getFullYear()} Social Bunkr. All rights reserved.
              </p>
              <div className="flex gap-6">
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-background/60 hover:text-secondary text-sm transition-colors"
                >
                  Terms
                </button>
                <Link to="#" className="text-background/60 hover:text-secondary text-sm transition-colors">
                  Privacy
                </Link>
                <Link to="#" className="text-background/60 hover:text-secondary text-sm transition-colors">
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
      <ToastContainer />

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-primary">Terms and Conditions</h3>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close terms and conditions"
              >
                <X />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto space-y-4">
              {termsSections.map((section) => (
                <div key={section.title}>
                  <h4 className="font-bold text-black">{section.title}</h4>
                  <p className="text-sm text-gray-700 mt-1">{section.body}</p>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2 text-sm font-bold text-gray-700 hover:text-black"
              >
                Close
              </button>
              <Button
                type="button"
                onClick={() => {
                  form.setValue('terms', true, { shouldValidate: true });
                  setShowTermsModal(false);
                }}
                className="bg-primary hover:bg-primary/90"
              >
                I Agree
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;
